import React from 'react';
import dayjs from 'dayjs';
import { get } from 'lodash-es';
import { CARE_ORDER_BABY_TYPE } from '@/interface/domain/careOrderBaby';
import { getIsTimeASameOrAfterTimeB } from '@/util/helper/dateTime';
import useDispatch from '@/util/hook/useDispatch';
import useMemo from '@/util/hook/useMemo';
import useSelector from '@/util/hook/useSelector';
import useState from '@/util/hook/useState';
import { getAllDayExpectedStartTimeDateOptionList } from '../../util/helper/option';
import { careOrderBabyFormUpdateAllDayTimeSlot, selectCareOrderBabyForm, } from '../../redux/module/careOrderBabyForm';
import { selectESTBufferHours, selectESTFurthestDays } from '../../redux/module/remoteConfig';
import View from './View';
import { POPUP_TYPE } from '../../config/popup';
import { TIME_MANIPULATION } from '../../config/time';
const getIsDateChoiceChipSelected = ({ expectedStartTime, dateOption, }) => {
    const expectedStartTimeDate = dayjs(expectedStartTime).date();
    const optionDate = dayjs(dateOption).date();
    const isSelected = expectedStartTimeDate === optionDate;
    return isSelected;
};
const getIsAddButtonAvailable = ({ expectedStartTime, isHour, }) => {
    const expectedStartTimeDateObj = dayjs(expectedStartTime);
    const timeUnitType = isHour ? TIME_MANIPULATION.HOUR.TYPE : TIME_MANIPULATION.MINUTE.TYPE;
    const timeUnit = isHour ? TIME_MANIPULATION.HOUR.UNIT : TIME_MANIPULATION.MINUTE.UNIT;
    const expectedStarTimeAfterAddHour = expectedStartTimeDateObj.add(timeUnit, timeUnitType);
    // The add button is available if time after addition is still within the range of the same day
    const isAvailable = expectedStarTimeAfterAddHour.date() === expectedStartTimeDateObj.date();
    return isAvailable;
};
const getIsSubtractButtonAvailable = ({ expectedStartTime, minExpectedStartTime, isHour, }) => {
    const expectedStartTimeDateObj = dayjs(expectedStartTime);
    const timeUnitType = isHour ? TIME_MANIPULATION.HOUR.TYPE : TIME_MANIPULATION.MINUTE.TYPE;
    const timeUnit = isHour ? TIME_MANIPULATION.HOUR.UNIT : TIME_MANIPULATION.MINUTE.UNIT;
    const expectedStartTimeAfterSubtractHour = expectedStartTimeDateObj.subtract(timeUnit, timeUnitType);
    const isMinDate = expectedStartTimeDateObj.date() === dayjs(minExpectedStartTime).date();
    // For the min date case, the subtract button is available if the subtracted time
    // is same or after the min expectedStartTime
    const isAvailableForMinDateCase = getIsTimeASameOrAfterTimeB({
        timeA: expectedStartTimeAfterSubtractHour.toISOString(),
        timeB: minExpectedStartTime,
    });
    // For dates other than the min date, the subtract button is available
    // if the subtracted time is still within the range of the same day
    const isAvailableForNormalCase = expectedStartTimeAfterSubtractHour.date() === expectedStartTimeDateObj.date();
    const isAvailable = isMinDate ? isAvailableForMinDateCase : isAvailableForNormalCase;
    return isAvailable;
};
const handleDateChoiceChipClick = ({ dispatch, date }) => {
    dispatch(careOrderBabyFormUpdateAllDayTimeSlot({
        expectedStartTime: date,
    }));
};
const handleAddButtonClick = ({ expectedStartTime, dispatch, isHour, }) => {
    const manipulationType = isHour ? TIME_MANIPULATION.HOUR.TYPE : TIME_MANIPULATION.MINUTE.TYPE;
    const manipulationUnit = isHour ? TIME_MANIPULATION.HOUR.UNIT : TIME_MANIPULATION.MINUTE.UNIT;
    const expectedStartTimeAfterAddition = dayjs(expectedStartTime)
        .add(manipulationUnit, manipulationType)
        .toISOString();
    dispatch(careOrderBabyFormUpdateAllDayTimeSlot({
        expectedStartTime: expectedStartTimeAfterAddition,
    }));
};
const handleSubtractButtonClick = ({ expectedStartTime, dispatch, isHour, }) => {
    const manipulationType = isHour ? TIME_MANIPULATION.HOUR.TYPE : TIME_MANIPULATION.MINUTE.TYPE;
    const manipulationUnit = isHour ? TIME_MANIPULATION.HOUR.UNIT : TIME_MANIPULATION.MINUTE.UNIT;
    const expectedStartTimeAfterAddition = dayjs(expectedStartTime)
        .subtract(manipulationUnit, manipulationType)
        .toISOString();
    dispatch(careOrderBabyFormUpdateAllDayTimeSlot({
        expectedStartTime: expectedStartTimeAfterAddition,
    }));
};
export default function Controller({ popupType, setPopupType }) {
    const [popupProgress, setPopupProgress] = useState(1); // one-indexed
    const careOrderBabyForm = useSelector(selectCareOrderBabyForm);
    const timeSlotList = careOrderBabyForm.timeSlotList;
    const allDayTimeSlot = timeSlotList[0];
    const expectedStartTime = get(allDayTimeSlot, 'expectedStartTime', '');
    const initialType = careOrderBabyForm.initialType;
    const ESTBufferHours = useSelector(selectESTBufferHours);
    const ESTFurthestDays = useSelector(selectESTFurthestDays);
    const dispatch = useDispatch();
    const expectedStartTimeDateOptionList = useMemo(() => {
        if (initialType !== CARE_ORDER_BABY_TYPE.ALL_DAY || !ESTBufferHours || !ESTFurthestDays)
            return [];
        return getAllDayExpectedStartTimeDateOptionList({
            ESTBufferHours,
            ESTFurthestDays,
        });
    }, [initialType, ESTBufferHours, ESTFurthestDays]);
    return (<View 
    // data
    popupProgress={popupProgress} expectedStartTimeDateOptionList={expectedStartTimeDateOptionList} expectedStartTime={expectedStartTime} popupType={popupType} 
    // func
    setPopupProgress={setPopupProgress} handleDateChoiceChipClick={({ date }) => handleDateChoiceChipClick({ dispatch, date })} handleAddButtonClick={({ isHour }) => handleAddButtonClick({ expectedStartTime, dispatch, isHour })} handleSubtractButtonClick={({ isHour }) => handleSubtractButtonClick({ expectedStartTime, dispatch, isHour })} getIsDateChoiceChipSelected={({ dateOption }) => getIsDateChoiceChipSelected({
            expectedStartTime,
            dateOption,
        })} getIsAddButtonAvailable={({ isHour }) => getIsAddButtonAvailable({ expectedStartTime, isHour })} getIsSubtractButtonAvailable={({ isHour }) => getIsSubtractButtonAvailable({
            expectedStartTime,
            minExpectedStartTime: expectedStartTimeDateOptionList[0],
            isHour,
        })} closePopup={() => setPopupType(POPUP_TYPE.CLOSE)}/>);
}
